<template>
  <div>
    <div :id="'printSummary_' + eocId" v-if="eoc && client" class="p-4">
      <div
        :class="windowWidth >= 1920 ? 'flex-nowrap' : 'flex-wrap'"
        class="d-flex align-items-start mb-4"
        style="gap: 20px"
      >
        <div class="d-flex flex-column aside-column" style="gap: 20px">
          <eoc-view-summary-demographics
            :eoc="eoc"
            :showing="showing"
            class="summary-module p-4"
            @onEdit="openEocForm"
          />
          <template v-if="windowWidth < 1920">
            <eoc-view-summary-billing
              v-show="showBilling && this.systemSetting.enablePaymentSystem"
              :patient="eoc.patient"
              :showing="showing"
              ref="billing"
              class="summary-module"
            />
            <eoc-view-summary-insurance
              v-show="showInsurances"
              :eoc="eoc"
              :showing="showing"
              ref="insurances"
              class="summary-module"
              @onEdit="openEocForm"
            />
            <eoc-view-summary-allergies
              v-show="showAllergies"
              :eoc="eoc"
              :showing="showing"
              class="summary-module"
              @onEdit="openEocForm"
            />
            <eoc-view-summary-contacts
              v-show="showContacts"
              :eoc="eoc"
              :showing="showing"
              class="summary-module"
              @onEdit="openEocForm"
            />
            <eoc-view-summary-guarantors
              v-show="showGuarantors"
              :eoc="eoc"
              :showing="showing"
              class="summary-module"
              @onEdit="openEocForm"
            />
            <eoc-view-summary-assigned-personnel
              v-show="showAssignedPersonel"
              :eoc="eoc"
              :showing="showing"
              ref="assignedPersonnel"
              class="summary-module"
              @onEdit="openEocForm"
              @shouldDisplay="e => (showAssignedPersonel = e)"
            />
            <eoc-view-summary-diagnosis-codes
              v-show="showDiagnosisCodes"
              :eoc="eoc"
              :showing="showing"
              ref="diagnosisCodes"
              class="summary-module"
              @shouldDisplay="e => (showDiagnosisCodes = e)"
            />
            <eoc-view-summary-next-appointments
              ref="nextEvents"
              :showing="showing"
              :episodeOfCareId="eoc.id"
              class="summary-module"
              @edit="editScheduleEvent"
            />
          </template>
        </div>
        <div class="d-flex flex-column main-column">
          <eoc-view-summary-alerts :showing="showing" :eoc="eoc" ref="alerts" />
          <eoc-view-summary-timeline
            :showing="showing"
            :eoc="eoc"
            class="summary-module"
          />
        </div>
        <div
          v-if="windowWidth >= 1920"
          :class="displayAsideColumn ? 'd-flex' : 'd-none'"
          class="flex-column aside-column"
          style="gap: 20px"
        >
          <eoc-view-summary-billing
            v-show="showBilling && this.systemSetting.enablePaymentSystem"
            :patient="eoc.patient"
            :showing="showing"
            ref="billing"
            class="summary-module"
          />
          <eoc-view-summary-next-appointments
            ref="nextEvents"
            :showing="showing"
            :episodeOfCareId="eoc.id"
            class="summary-module"
            @edit="editScheduleEvent"
          />
          <eoc-view-summary-insurance
            v-show="showInsurances"
            :eoc="eoc"
            :showing="showing"
            ref="insurances"
            class="summary-module"
            @onEdit="openEocForm"
          />
          <eoc-view-summary-allergies
            v-show="showAllergies"
            :eoc="eoc"
            :showing="showing"
            class="summary-module"
            @onEdit="openEocForm"
          />
          <eoc-view-summary-contacts
            v-show="showContacts"
            :eoc="eoc"
            :showing="showing"
            class="summary-module"
            @onEdit="openEocForm"
          />
          <eoc-view-summary-guarantors
            v-show="showGuarantors"
            :eoc="eoc"
            :showing="showing"
            class="summary-module"
            @onEdit="openEocForm"
          />
          <eoc-view-summary-assigned-personnel
            v-show="showAssignedPersonel"
            :eoc="eoc"
            :showing="showing"
            ref="assignedPersonnel"
            class="summary-module"
            @onEdit="openEocForm"
            @shouldDisplay="e => (showAssignedPersonel = e)"
          />
          <eoc-view-summary-diagnosis-codes
            v-show="showDiagnosisCodes"
            :eoc="eoc"
            :showing="showing"
            ref="diagnosisCodes"
            class="summary-module"
            @shouldDisplay="e => (showDiagnosisCodes = e)"
          />
        </div>
      </div>
    </div>
    <eoc-add-edit-modal
      v-if="showAddEditEocModal"
      v-model="showAddEditEocModal"
      :clientId="clientId"
      :eocId="eocId"
      :initialTab="eocFormInitialTab"
      @onModify="fetchData"
    />
    <appointment-add-edit-modal
      v-if="appointmentEditor.show"
      v-model="appointmentEditor.show"
      :scheduleEventId="appointmentEditor.id"
      @onModify="refetchNextAppointments"
      @onHidden="resetsEditData"
    />
    <group-session-add-edit-modal
      v-if="groupEditor.show"
      v-model="groupEditor.show"
      :groupId="groupEditor.id"
      @onModify="refetchNextAppointments"
      @onHidden="resetsEditData"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import EocViewSummaryDemographics from "./EocViewSummaryDemographics.vue";
import EocViewSummaryInsurance from "./EocViewSummaryInsurance.vue";
import EocViewSummaryAllergies from "./EocViewSummaryAllergies.vue";
import EocViewSummaryContacts from "./EocViewSummaryContacts.vue";
import EocViewSummaryGuarantors from "./EocViewSummaryGuarantors.vue";
import EocViewSummaryAssignedPersonnel from "./EocViewSummaryAssignedPersonnel.vue";
import EocViewSummaryAlerts from "./EocViewSummaryAlerts.vue";
import EocViewSummaryTimeline from "./EocViewSummaryTimeline.vue";
import EocViewSummaryDiagnosisCodes from "./EocViewSummaryDiagnosisCodes.vue";
import EocViewSummaryBilling from "./EocViewSummaryBilling.vue";
import EocViewSummaryNextAppointments from "./EocViewSummaryNextAppointments.vue";
import AppointmentAddEditModal from "../scheduler/AppointmentAddEditModal.vue";
import GroupSessionAddEditModal from "../group_session/GroupSessionAddEditModal.vue";
import EocAddEditModal from "./EocAddEditModal.vue";
import eocServer from "../../server/eoc";
import patientServer from "../../server/patient";

export default {
  name: "EocViewSummary",
  components: {
    EocViewSummaryDemographics,
    EocViewSummaryInsurance,
    EocViewSummaryAllergies,
    EocViewSummaryContacts,
    EocViewSummaryGuarantors,
    EocViewSummaryAssignedPersonnel,
    EocViewSummaryAlerts,
    EocViewSummaryTimeline,
    EocViewSummaryDiagnosisCodes,
    EocAddEditModal,
    EocViewSummaryBilling,
    EocViewSummaryNextAppointments,
    AppointmentAddEditModal,
    GroupSessionAddEditModal
  },
  props: ["eocId", "clientId", "showing"],
  data() {
    return {
      eoc: null,
      client: null,
      eocFormInitialTab: 0,
      showAddEditEocModal: false,
      windowWidth: window.innerWidth,
      showAssignedPersonel: false,
      showDiagnosisCodes: false,
      appointmentEditor: {
        show: false,
        id: null
      },
      groupEditor: {
        show: false,
        id: null
      }
    };
  },
  mounted() {
    this.fetchData();
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    ...mapMutations("client", ["dispatchListener"]),
    async fetchData() {
      this.showAddEditEocModal = false;
      await this.getClient();
      await this.getEoc();
      this.dispatchAssignedPersonnel();
      this.dispatchInsurances();
      this.dispatchBilling();
      this.dispatchAlerts();
      this.$refs["diagnosisCodes"].fetchDiagnosisCodes();
      this.dispatchListener({ listener: "fetchClient", param: this.clientId });
      this.dispatchListener({ listener: "fetchEoc", param: "all" });
      this.refetchNextAppointments();
    },
    dispatchAssignedPersonnel() {
      if (this.$refs.assignedPersonnel) {
        this.$refs.assignedPersonnel.fetchCaseLoads();
      }
    },
    dispatchInsurances() {
      if (this.$refs.insurances) {
        this.$refs.insurances.getInsuranceData();
      }
    },
    dispatchBilling() {
      if (this.$refs.billing) {
        this.$refs.billing.fetchBillingSummary();
      }
    },
    dispatchAlerts() {
      if (this.$refs.alerts) {
        this.$refs.alerts.fetchAlerts();
      }
    },
    async getEoc() {
      this.eoc = await eocServer.get(this.eocId);
      this.allergies = this.eoc.patient.allergies;
      this.insurances = this.eoc.patient.insurances;
    },
    async getClient() {
      this.client = await patientServer.get(this.clientId);
    },
    printSummary() {
      setTimeout(() => {
        this.$htmlToPaper("printSummary_" + this.eocId);
      }, 500);
    },
    openEocForm(initialTab = 0) {
      this.eocFormInitialTab = initialTab;
      this.showAddEditEocModal = true;
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    editScheduleEvent({ id, type }) {
      switch (type) {
        case "GroupSession":
          this.groupEditor.id = id;
          this.groupEditor.show = true;
          break;

        default:
          this.appointmentEditor.id = id;
          this.appointmentEditor.show = true;
      }
    },
    async refetchNextAppointments() {
      await this.$refs.nextEvents.fetchNextAppointments();
      this.resetsEditData();
    },
    resetsEditData() {
      this.appointmentEditor.id = null;
      this.groupEditor.id = null;
      this.appointmentEditor.show = false;
      this.groupEditor.show = false;
    }
  },
  computed: {
    ...mapState("layout", { systemSetting: state => state.systemSetting }),
    ...mapGetters("layout", ["hasCurrentUserPermissionAccess"]),
    ...mapGetters("client", ["listeners"]),
    showInsurances() {
      return !!this.eoc?.patient.insurances.length;
    },
    showAllergies() {
      return !!this.eoc?.patient.allergies.length;
    },
    showContacts() {
      return !!this.eoc?.patient.contacts.length;
    },
    showGuarantors() {
      return !!this.eoc?.patient.guarantors.length;
    },
    showBilling() {
      return this.hasCurrentUserPermissionAccess(
        "others",
        "patientBillingInfo"
      );
    },
    displayAsideColumn() {
      return (
        this.showInsurances ||
        this.showAllergies ||
        this.showContacts ||
        this.showGuarantors ||
        this.showAssignedPersonel ||
        this.showDiagnosisCodes ||
        this.showBilling
      );
    }
  },
  watch: {
    "listeners.fetchClient": function(newValue, oldValue) {
      if (newValue[this.clientId] != oldValue[this.clientId]) {
        this.getClient();
      }
    },
    "listeners.fetchEoc": function(newValue, oldValue) {
      if (newValue[this.eocId] != oldValue[this.eocId]) {
        this.fetchData();
      }
    },
    showing: async function(newValue) {
      if (newValue) {
        this.fetchData();
      }
    }
  }
};
</script>

<style scoped>
.summary-module {
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: white;
}

.aside-column,
.main-column {
  width: 100%;
}

@media (min-width: 1470px) {
  .aside-column {
    max-width: 350px;
  }

  .main-column {
    max-width: calc(100% - 400px);
  }
}

@media (min-width: 1920px) {
  .main-column {
    max-width: unset;
    flex-grow: 1;
  }
}
</style>
